import store from '@/store'
import { useLambda } from '@/views/habit/awsLambda'
import endpoints from '@/libs/endpoints'
import i18n from '@/libs/i18n'
import { oid, date } from '@/helpers/mongoHelpers'
import { LOGO_CORREO } from '@/libs/emailAssets'
import { hasPermission } from '@/composables/usePermission'
import useNotifications from '@/composables/useNotifications'
import useAuditLog from '@/composables/useAuditLog'

export default function useCommonLibrary() {
  const {
    createItem, updateItem, getItem, getPresignedUploadUrl, getPresignedDownloadUrl, getLatestVersion, sendEmail,
  } = useLambda()
  const userData = store.state?.userStore?.userData
  const { showErrorMessage } = useNotifications()
  const { logAction, addAuditEntry: addAuditEntryUnified } = useAuditLog()
  const collection = 'library_document'

  // Validar/Rechazar están disponibles si el documento está pendiente Y (el usuario es el
  // responsable de ESE documento — identidad por registro — O tiene el permiso general de
  // Editar). Compartido entre View.vue y el listado para no duplicar el cálculo.
  const canValidateDocument = document => {
    if (document.estado !== 'pendiente') return false
    // eslint-disable-next-line no-underscore-dangle
    const currentUserId = userData.worker_id?.$oid || userData._id
    const responsableId = document.responsable?.$oid || document.responsable?.toString() || ''
    const isResponsable = currentUserId === responsableId
    return isResponsable || hasPermission('records-library', 'edit')
  }

  // Wrapper para mantener compatibilidad con código existente
  // Usa el sistema unificado con campo 'history'
  const addAuditEntry = async (documentId, accion, detalle) => {
    await logAction({
      collection,
      documentId,
      action: accion,
      detail: detalle,
    })
  }

  const uploadFileToS3 = async (file, predefinedKey) => {
    try {
      const destinationFolder = predefinedKey ? undefined : `${userData.client.$oid}/library`
      const { uploadUrl, key } = await getPresignedUploadUrl({
        fileName: file.name,
        fileType: file.type,
        destinationFolder,
        key: predefinedKey,
      })

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      const { versionId, etag } = await getLatestVersion({ key })

      return { key, versionId, etag }
    } catch (error) {
      console.log(error)
      throw new Error(error)
    }
  }

  const downloadVersion = async (fileKey, versionId) => {
    const versionToSend = versionId === 'null' ? undefined : versionId
    const { url } = await getPresignedDownloadUrl({ key: fileKey, versionId: versionToSend })
    window.open(url, '_blank')
  }

  const createDocument = async (payload, file) => {
    if (!hasPermission('records-library', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    // eslint-disable-next-line no-param-reassign
    payload.client_id = oid(userData.client.$oid)
    // eslint-disable-next-line no-param-reassign
    payload.registradoPor = oid(userData.worker_id?.$oid || userData._id)
    // eslint-disable-next-line no-param-reassign
    payload.fechaRegistro = date(new Date())
    // eslint-disable-next-line no-param-reassign
    payload.estado = 'pendiente'
    // eslint-disable-next-line no-param-reassign
    payload.versionActual = 1
    // eslint-disable-next-line no-param-reassign
    payload.activo = true
    // eslint-disable-next-line no-param-reassign
    payload.historialVersiones = []
    // eslint-disable-next-line no-param-reassign
    payload.history = [] // Sistema unificado de audit logs

    if (!payload.fechaProximaRevision) {
      const defaultDate = new Date()
      defaultDate.setMonth(defaultDate.getMonth() + 12)
      // eslint-disable-next-line no-param-reassign
      payload.fechaProximaRevision = date(defaultDate)
    }

    const { insertedId } = await createItem({ collection, payload })
    const documentId = insertedId.toString()

    if (file) {
      const { key, versionId, etag } = await uploadFileToS3(file)

      const versionEntry = {
        numeroVersion: 1,
        nombreArchivo: file.name,
        archivo: key,
        versionId,
        etag,
        subidoPor: oid(userData.worker_id?.$oid || userData._id),
        fechaSubida: date(new Date()),
      }

      await updateItem({
        collection,
        query: { _id: oid(documentId) },
        action: {
          $set: { fileLink: key, version: `${documentId}-${versionId}` },
          $push: { historialVersiones: versionEntry },
        },
      })
    }

    await addAuditEntry(documentId, 'crear')

    return documentId
  }

  const updateDocumentMetadata = async (documentId, payload, originalData = null, excludeFields = []) => {
    if (!hasPermission('records-library', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: { $set: payload },
    })

    // Si tenemos originalData, detectar cambios con sistema unificado
    if (originalData) {
      await addAuditEntryUnified({
        collection,
        documentId: oid(documentId),
        originalData,
        updatedData: payload,
        action: 'editar_metadatos',
        excludeFields,
      })
    } else {
      // Fallback para compatibilidad (sin detección de cambios)
      await addAuditEntry(documentId, 'editar_metadatos')
    }
  }

  const uploadNewVersion = async (documentId, file, currentDoc) => {
    if (!hasPermission('records-library', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    const newVersionNumber = (currentDoc.versionActual || 1) + 1
    const existingKey = currentDoc.fileLink

    const { key, versionId, etag } = await uploadFileToS3(file, existingKey)

    const versionEntry = {
      numeroVersion: newVersionNumber,
      nombreArchivo: file.name,
      archivo: key,
      versionId,
      etag,
      subidoPor: oid(userData.worker_id?.$oid || userData._id),
      fechaSubida: date(new Date()),
    }

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: {
        $set: {
          versionActual: newVersionNumber,
          version: `${documentId}-${versionId}`,
          estado: 'pendiente',
        },
        $push: { historialVersiones: versionEntry },
      },
    })

    await addAuditEntry(documentId, 'subir_nueva_version')

    return newVersionNumber
  }

  const deactivateDocument = async documentId => {
    if (!hasPermission('records-library', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: { $set: { activo: false } },
    })

    await addAuditEntry(documentId, 'desactivar')
  }

  const validateDocument = async (documentId, fechaProximaRevision) => {
    if (!hasPermission('records-library', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    const now = new Date()
    const fechaRevision = fechaProximaRevision || new Date(now.setMonth(now.getMonth() + 12))

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action: {
        $set: {
          estado: 'validado',
          validadoPor: oid(userData.worker_id?.$oid || userData._id),
          fechaValidacion: date(new Date()),
          fechaProximaRevision: date(fechaRevision),
        },
      },
    })

    await addAuditEntry(documentId, 'validar')
  }

  const rejectDocument = async (documentId, motivoRechazo, fechaProximaRevision) => {
    if (!hasPermission('records-library', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      throw new Error('not authorized')
    }

    const action = {
      $set: {
        estado: 'rechazado',
        motivoRechazo,
        rechazadoPor: oid(userData.worker_id?.$oid || userData._id),
        fechaRechazo: date(new Date()),
      },
    }

    if (fechaProximaRevision) {
      action.$set.fechaProximaRevision = date(fechaProximaRevision)
    }

    await updateItem({
      collection,
      query: { _id: oid(documentId), client_id: oid(userData.client.$oid) },
      action,
    })

    await addAuditEntry(documentId, 'rechazar')
  }

  const getLibraryEmailTemplate = ({ recipientName, textMessage, documentName, tipoDocumento, versionActual, estado, fechaProximaRevision, extraInfo, documentId: docId }) => {
    // Normalizar fecha: extraer el valor si es {$date: "..."} o {$date: {$numberLong: "..."}}
    let fechaFormatted = ''
    if (fechaProximaRevision) {
      let dateValue = fechaProximaRevision
      if (fechaProximaRevision.$date) {
        dateValue = fechaProximaRevision.$date.$numberLong || fechaProximaRevision.$date
      }
      try {
        fechaFormatted = new Date(dateValue).toLocaleDateString('es-CL', { timeZone: 'UTC' })
      } catch (e) {
        fechaFormatted = String(dateValue)
      }
    }

    return `
    <html>
      <body style="margin: 0; padding: 0; background-color: #ffffff;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #ffffff;">
          <tr>
            <td align="center" style="padding: 20px 10px;">
              <table width="600" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border-radius: 10px; overflow: hidden;">
                <tr>
                  <td style="background-color: #ecf0f1; text-align: center; padding: 20px 0 10px;">
                    <img width="120" src="${LOGO_CORREO}" alt="AddCo Logo">
                  </td>
                </tr>
                <tr>
                  <td style="background-color: #ecf0f1; padding: 20px 40px 30px; color: #34495e; font-family: sans-serif;">
                    <h2 style="color: #e67e22; margin: 0 0 7px;">
                      ${i18n.t('message.hello')}${recipientName ? ` ${recipientName}` : ''},
                    </h2>
                    <p style="margin: 2px 0; font-size: 15px;">
                      ${textMessage}
                    </p>
                    <ul style="font-size: 15px; margin: 10px 0; padding-left: 20px;">
                      <li><u>${i18n.t('biblioteca.nombre')}</u>: <b>${documentName}</b></li>
                      ${tipoDocumento ? `<li><u>${i18n.t('biblioteca.tipoDocumento')}</u>: <b>${tipoDocumento}</b></li>` : ''}
                      ${versionActual ? `<li><u>${i18n.t('biblioteca.versionActual')}</u>: <b>v${versionActual}</b></li>` : ''}
                      ${estado ? `<li><u>${i18n.t('biblioteca.estado')}</u>: <b>${i18n.t('biblioteca.estado_' + estado)}</b></li>` : ''}
                      ${fechaFormatted ? `<li><u>${i18n.t('biblioteca.fechaProximaRevision')}</u>: <b>${fechaFormatted}</b></li>` : ''}
                      ${extraInfo ? `<li>${extraInfo}</li>` : ''}
                    </ul>
                    <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0 10px;">
                      <tr>
                        <td align="center">
                          <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #498ceb; font-size: 17px; display: inline-block;" href="${endpoints.app_url}/organization/library/view/${docId}">
                            ${i18n.t('biblioteca.email_see_document')}
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `
  }

  const getWorkerById = async (workerId) => {
    const id = workerId?.$oid || workerId?.toString() || workerId
    if (!id) return null
    return getItem({ collection: 'worker', query: { _id: oid(id) } })
  }

  const sendLibraryEmail = async ({ recipientWorkerId, subject, textMessage, documentName, tipoDocumento, versionActual, estado, fechaProximaRevision, extraInfo, documentId: docId }) => {
    try {
      const worker = await getWorkerById(recipientWorkerId)
      if (!worker?.email) return

      const htmlBody = getLibraryEmailTemplate({
        recipientName: worker.name,
        textMessage,
        documentName,
        tipoDocumento,
        versionActual,
        estado,
        fechaProximaRevision,
        extraInfo,
        documentId: docId,
      })

      const recipientEmail = worker.realEmail || worker.email
      await sendEmail({ to: [recipientEmail], subject, htmlBody })
      await addAuditEntry(docId, 'envio_email', `${worker.name} (${recipientEmail})`)
    } catch (error) {
      console.log(error)
    }
  }

  const sendDocumentCreatedEmail = (docId, doc, responsableId) => {
    sendLibraryEmail({
      recipientWorkerId: responsableId,
      subject: i18n.t('biblioteca.email_subject_created'),
      textMessage: i18n.t('biblioteca.email_text_created'),
      documentName: doc.nombre,
      tipoDocumento: doc.tipoDocumento,
      versionActual: doc.versionActual || 1,
      estado: 'pendiente',
      fechaProximaRevision: doc.fechaProximaRevision,
      documentId: docId,
    })
  }

  const sendNewVersionEmail = (docId, doc, versionNumber, responsableId) => {
    sendLibraryEmail({
      recipientWorkerId: responsableId,
      subject: i18n.t('biblioteca.email_subject_new_version'),
      textMessage: i18n.t('biblioteca.email_text_new_version', { version: versionNumber }),
      documentName: doc.nombre,
      tipoDocumento: doc.tipoDocumento,
      versionActual: versionNumber,
      estado: 'pendiente',
      fechaProximaRevision: doc.fechaProximaRevision,
      documentId: docId,
    })
  }

  const sendValidatedEmail = (docId, doc) => {
    sendLibraryEmail({
      recipientWorkerId: doc.registradoPor,
      subject: i18n.t('biblioteca.email_subject_validated'),
      textMessage: i18n.t('biblioteca.email_text_validated'),
      documentName: doc.nombre,
      tipoDocumento: doc.tipoDocumento,
      versionActual: doc.versionActual,
      estado: 'validado',
      fechaProximaRevision: doc.fechaProximaRevision,
      documentId: docId,
    })
  }

  const sendRejectedEmail = (docId, doc, motivoRechazo) => {
    sendLibraryEmail({
      recipientWorkerId: doc.registradoPor,
      subject: i18n.t('biblioteca.email_subject_rejected'),
      textMessage: i18n.t('biblioteca.email_text_rejected'),
      documentName: doc.nombre,
      tipoDocumento: doc.tipoDocumento,
      versionActual: doc.versionActual,
      estado: 'rechazado',
      fechaProximaRevision: doc.fechaProximaRevision,
      extraInfo: `<u>${i18n.t('biblioteca.rejection_reason')}</u>: <b>${motivoRechazo}</b>`,
      documentId: docId,
    })
  }

  const sendMetadataEditedEmail = (docId, doc, responsableId, isNewResponsable) => {
    const subject = isNewResponsable
      ? i18n.t('biblioteca.email_subject_created')
      : i18n.t('biblioteca.email_subject_metadata_edited')
    const textMessage = isNewResponsable
      ? i18n.t('biblioteca.email_text_created')
      : i18n.t('biblioteca.email_text_metadata_edited')

    sendLibraryEmail({
      recipientWorkerId: responsableId,
      subject,
      textMessage,
      documentName: doc.nombre,
      tipoDocumento: doc.tipoDocumento,
      versionActual: doc.versionActual,
      estado: doc.estado,
      fechaProximaRevision: doc.fechaProximaRevision,
      documentId: docId,
    })
  }

  return {
    uploadFileToS3,
    downloadVersion,
    createDocument,
    updateDocumentMetadata,
    uploadNewVersion,
    deactivateDocument,
    validateDocument,
    rejectDocument,
    canValidateDocument,
    addAuditEntry,
    sendDocumentCreatedEmail,
    sendNewVersionEmail,
    sendValidatedEmail,
    sendRejectedEmail,
    sendMetadataEditedEmail,
    collection,
  }
}
